import type { ReactNode } from 'react'
import { cx, sva } from '~/styled-system/css'

type Props = {
  title: ReactNode
  annotation?: ReactNode
  annotationPosition?: 'right' | 'top'
  headerAlignItems?: 'center' | 'top'
  sectionClass?: string
  bodyClass?: string
  children: ReactNode
}

const styles = sva({
  slots: ['container', 'heading', 'title', 'annotation', 'content'],
  base: {
    container: {
      spaceY: '3',
      bg: 'white',
      px: '3',
      py: '4',
      _pc: {
        spaceY: '0',
        p: '6',
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'space-between',
      },
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'row',
    },
    title: {
      fontSize: 'xl',
      fontWeight: 'bold',
      _pc: {
        fontSize: '2xl',
      },
    },
    annotation: {
      order: '1',
    },
    content: {
      _pc: {
        w: '[424px]',
        ml: '8',
      },
    },
  },
  variants: {
    annotationPosition: {
      top: {
        heading: {
          flexDirection: 'column',
          alignItems: 'start',
        },
        annotation: {
          order: -1,
        },
      },
      right: {
        heading: {
          flexDirection: 'row',
        },
        annotation: {
          order: 1,
        },
      },
    },
    headerAlignItems: {
      center: {
        heading: {
          alignItems: 'center',
        },
      },
      top: {
        heading: {
          alignItems: 'start',
        },
      },
    },
  },
  defaultVariants: {
    annotationPosition: 'right',
    headerAlignItems: 'center',
  },
})

export function Section({
  title,
  annotation,
  annotationPosition = 'right',
  headerAlignItems,
  sectionClass,
  bodyClass,
  children,
}: Props) {
  const classes = styles({ annotationPosition, headerAlignItems })
  return (
    <div className={cx(classes.container, sectionClass)}>
      <div className={classes.heading}>
        <h2 className={classes.title}>{title}</h2>
        {annotation && <div className={classes.annotation}>{annotation}</div>}
      </div>
      <div className={cx(classes.content, bodyClass)}>{children}</div>
    </div>
  )
}
